import {
  BillingFrequency,
  EnterpriseAccountMetaData,
  EnterpriseAccountStatus,
  Portfolio,
} from "@/common/common";
import { FileUploadConfig, UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { Language } from "@/i18n/languages";
import { GetMeetingEmailDocumentResponse } from "@/noticeForBoardMember/noticeForBoardMemberDomain";
import {
  PortfolioShareholdersResponse,
  SelectedPortfolioOption,
  toPortfolioApiUrl,
  toPortfolioContactsApiUrl,
} from "@/portfolio/portfolioDomain";
import { BeneficialOwner } from "@/shareholderRegister/beneficialOwners/beneficialOwnersDomain";
import {
  CreateShareholderCommitmentRequest,
  ShareholderCommitment,
  ShareholderContribution,
  ShareholderPledgeForm,
  ShareholderRemarksForm,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { DocumentsToSignResponse } from "@/signing/signDocumentDomain";
import { CompanyInfo, SuggestedCompany } from "@/types/CompanyInfo";
import {
  ParsedShareholder,
  ParseExcelResponse,
  ShareholderIdShares,
} from "@/types/excelImportDomain";
import {
  BatchInvitation,
  CreateUserInvitation,
  Invitation,
  InvitationRequest,
  InvitationType,
} from "@/types/Invitation";
import {
  ChangePermissionsRequest,
  GetPermissionsResponse,
  ObjectPermission,
} from "@/types/Permissions";
import { Actor, SelectedActor } from "@/types/SelectActors";
import {
  GetAdminsInCompaniesResponse,
  GetPortfolioCustomRoleRequest,
  RevokeAllRequest,
  UpdateAdminEmailRequest,
} from "@/types/Settings";
import {
  ExtendedShareholderDTO,
  NewShareholderResponse,
  ShareholderDetailsResponseDTO,
  ShareholderField,
  ShareholdersInfo,
  ShareholderType,
  ShareholderViewResponse,
  UpdateShareholderDTO,
} from "@/types/Shareholder";
import {
  CancellationOfShares,
  ConversionFromISINDTO,
  ConversionOfSharesDTO,
  IncreaseNominalValueDTO,
  IssueNewSharesDTO,
  NewTransaction,
  PayDividend,
  ReductionOfNominalValue,
  RegisterGroupContribution,
  ShareholdingPerDateDTO,
  ShareReverseSplit,
  ShareSplitTransaction,
  Transaction,
  TransactionAllShares,
} from "@/types/Transactions";
import { LoggedOnUserInfoDTO, SsoAccountSelection, SsoConfirmResult, User } from "@/types/UserInfo";
import { toQueryString } from "@/utils/queryParams";
import {
  GetSubscriptionFormSigningStatusResponse,
  SignSubscriptionForm,
  SignSubscriptionFormShare,
} from "@/views/Pages/SubscriptionFormSign/subscriptionFormDomain";
import { AxiosInstance, AxiosRequestConfig } from "axios";
import { downloadFileContent } from "./DownloadService";

export class ApiService {
  constructor(readonly http: AxiosInstance) {}

  createUser(
    name: string,
    password: string,
    invitation: CreateUserInvitation,
    csrfToken: string,
  ): Promise<{ user: User; redirectTo: string }> {
    return this.http
      .post(`/auth/user/new`, { "X-CSRF-TOKEN": csrfToken, name, password, invitation })
      .then((res) => res.data);
  }

  logon(username: string, password: string, csrfToken: string): Promise<{ user: User }> {
    return this.http
      .post(`/auth/login`, { "X-CSRF-TOKEN": csrfToken, username, password })
      .then((res) => res.data);
  }

  async passwordResetRequest(email: string): Promise<void> {
    await this.http.post("/auth/login/password-reset-request", { email });
  }

  async checkPasswordResetToken(token: string): Promise<void> {
    await this.http.post("/auth/login/password-reset-check-token", { token });
  }

  async passwordResetSetNewPassword(token: string, newPassword: string): Promise<void> {
    const res = await this.http.post("/auth/login/password-reset-set-new-password", {
      token,
      newPassword,
    });
    return res.data;
  }

  postOneTimeCode(otp: string): Promise<void> {
    return this.http.post("/auth/login/otp", { otp }).then((res) => res.data);
  }

  sendNewOneTimeCode(): Promise<void> {
    return this.http.post("/auth/login/send-new-otp").then((res) => res.data);
  }

  authStatus(): Promise<void> {
    return this.http.get("/auth/status").then((res) => res.data);
  }

  getCsrfToken(headers?: AxiosRequestConfig["headers"]): Promise<string> {
    return this.http.get(`/index`, { headers }).then((res) => res.data);
  }

  fetchUserInfo(headers?: AxiosRequestConfig["headers"]): Promise<LoggedOnUserInfoDTO> {
    return this.http.get(`/auth/getloggedonuser`, { headers }).then((res) => res.data);
  }

  fetchSsoCandidates(): Promise<SsoAccountSelection> {
    return this.http.get("/auth/sso-candidates").then((res) => res.data);
  }

  confirmSsoSelection(externalId: string | null): Promise<SsoConfirmResult> {
    return this.http.post("/auth/sso-confirm", { externalId }).then((res) => res.data);
  }

  fetchCompanyInfo(
    orgNumber: string,
    options: { updateLastVisited?: boolean } = {},
  ): Promise<CompanyInfo> {
    return this.http.get(`/company/${orgNumber}${toQueryString(options)}`).then((res) => res.data);
  }

  fetchShareholderView(
    orgNumber: string,
    shareholderId: number,
    options: { updateLastVisited?: boolean } = {},
  ): Promise<ShareholderViewResponse> {
    return this.http
      .get(`/shareholder-view/${orgNumber}/${shareholderId}${toQueryString(options)}`)
      .then((res) => res.data);
  }

  updateShareholderInfo(shareholderId: number, shareholderDTO: UpdateShareholderDTO) {
    return this.http
      .post(`/shareholder-view/update-shareholder/${shareholderId}`, shareholderDTO)
      .then((res) => res.data);
  }

  fetchMeetingForBoardMember(
    orgNo: string,
    projectId: string,
  ): Promise<GetMeetingEmailDocumentResponse> {
    return this.http
      .get(`/boardmember-view/${orgNo}/meetings/${projectId}`)
      .then((res) => res.data);
  }

  logout(): Promise<void> {
    return this.http.get(`/auth/logout`).then((res) => res.data);
  }

  fetchShareholdersAll(orgNo: string, timestamp?: string): Promise<ShareholdersInfo> {
    return this.http
      .get(`/shareholder/all/${orgNo}`, timestamp ? { params: { timestamp } } : undefined)
      .then((res) => res.data);
  }

  fetchShareholdersAsActors(orgNo: string): Promise<Actor[]> {
    return this.http.get(`/shareholder/${orgNo}/actors`).then((res) => res.data);
  }

  addShareholdersFromActors(
    orgNo: string,
    actors: SelectedActor[],
  ): Promise<ExtendedShareholderDTO[]> {
    return this.http.post(`/shareholder/${orgNo}/actors`, actors).then((res) => res.data);
  }

  runAutoimport(orgNumber: string | number): Promise<void> {
    return this.http.post(`/company/reimportshaholderdata/${orgNumber}`).then((res) => res.data);
  }

  async fetchShareholderById(
    id: number,
    timestamp?: string,
  ): Promise<ShareholderDetailsResponseDTO> {
    return this.http
      .get(`/shareholder/${id}`, timestamp ? { params: { timestamp } } : undefined)
      .then((res) => res.data);
  }

  fetchTransactionLog(orgNumber: string | number): Promise<Transaction[]> {
    return this.http.get(`/transactionlog/all/${orgNumber}`).then((res) => res.data);
  }

  fetchTransactionById(id: string | number): Promise<Transaction> {
    return this.http.get(`/transactionlog/${id}`).then((res) => res.data);
  }

  async getCompanies(partName: string): Promise<SuggestedCompany[]> {
    return this.http.get(`/companysearch`, { params: { q: partName } }).then((res) => res.data);
  }

  async createTransfer(transaction: NewTransaction, transactionId?: number): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "transfer"), transaction)
      .then((r) => r.data);
  }

  async transferAllShares(
    transaction: TransactionAllShares,
    transactionId?: number,
  ): Promise<number> {
    const response = await this.http.post(
      `/transaction/` + (transactionId ? transactionId : "sellall"),
      transaction,
    );
    return response.data;
  }

  increaseNominalValue(
    transaction: IncreaseNominalValueDTO,
    transactionId?: string,
  ): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "increasenominalvalue"}`, transaction)
      .then((res) => res.data);
  }

  issueShares(transaction: IssueNewSharesDTO, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "issue"}`, transaction)
      .then((res) => res.data);
  }

  shareSplit(transaction: ShareSplitTransaction, transactionId?: number): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "sharesplit"), transaction)
      .then((res) => res.data);
  }

  shareReverseSplit(transaction: ShareReverseSplit, transactionId?: number): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "sharereversesplit"), transaction)
      .then((res) => res.data);
  }

  shareCancellation(transaction: CancellationOfShares, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "sharecancellation"}`, transaction)
      .then((res) => res.data);
  }

  reductionOfNominalValue(
    transaction: ReductionOfNominalValue,
    transactionId?: string,
  ): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "reductionofnominalvalue"}`, transaction)
      .then((res) => res.data);
  }

  payDividend(transaction: PayDividend, transactionId?: number): Promise<number> {
    return this.http
      .post(`/transaction/` + (transactionId ? transactionId : "paydividend"), transaction)
      .then((res) => res.data);
  }

  registerGroupContribution(
    transaction: RegisterGroupContribution,
    transactionId?: string,
  ): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "registergroupcontribution"}`, transaction)
      .then((res) => res.data);
  }

  conversionOfShares(transaction: ConversionOfSharesDTO, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "conversionofshares"}`, transaction)
      .then((res) => res.data);
  }

  conversionFromISIN(transaction: ConversionFromISINDTO, transactionId?: string): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "conversionfromisin"}`, transaction)
      .then((res) => res.data);
  }

  shareholdingPerDate(
    transaction: ShareholdingPerDateDTO,
    transactionId?: string,
  ): Promise<number> {
    return this.http
      .post(`/transaction/${transactionId ?? "shareholdingperdate"}`, transaction)
      .then((res) => res.data);
  }

  uploadTransactionAttachment(
    transactionlogId: number,
    form: FormData,
  ): Promise<UploadedAttachment[]> {
    return this.http
      .post(`/transactionlog/${transactionlogId}/upload-attachment`, form)
      .then((res) => res.data);
  }

  deleteTransactionAttachment(transactionlogId: number, attachmentId: string): Promise<void> {
    return this.http
      .delete(`/transactionlog/${transactionlogId}/attachment/${attachmentId}`)
      .then((res) => res.data);
  }

  getDocumentsToSign(): Promise<DocumentsToSignResponse> {
    return this.http.get(`/sign/documents-to-sign`).then((res) => res.data);
  }

  getSigningBatchDocuments(batchId: string): Promise<DocumentsToSignResponse> {
    return this.http.get(`/sign/batch/${batchId}`).then((res) => res.data);
  }

  getSubscriptionFormDetails(documentReference: string): Promise<SignSubscriptionForm> {
    return this.http
      .get(`/sign/subscription-form-details/${documentReference}`)
      .then((res) => res.data);
  }

  getSubscriptionFormSigningStatus(
    documentReference: string,
  ): Promise<GetSubscriptionFormSigningStatusResponse> {
    return this.http
      .get(`/sign/check-subscription-signing-status/${documentReference}`)
      .then((res) => res.data);
  }

  updateSubscriptionFormDetails(
    documentReference: string,
    subscription: SignSubscriptionFormShare[],
  ): Promise<void> {
    return this.http
      .post(`/sign/update-subscription-form/${documentReference}`, subscription)
      .then((res) => res.data);
  }

  signWithEmail(documentReference: string): Promise<void> {
    return this.http.get(`/sign/sign-with-email/${documentReference}`).then((res) => res.data);
  }

  signVerifyWithCode(documentReference: string, authorizationCode: string): Promise<void> {
    return this.http
      .post(`/sign/verify-signing-with-code/${documentReference}/${authorizationCode}`)
      .then((res) => res.data);
  }

  signWithHandWriting(documentReference: string, formData: FormData): Promise<void> {
    return this.http
      .post(`/sign/sign-with-handwriting/${documentReference}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data);
  }

  fetchPermissions(orgNo: string, objectId: string): Promise<GetPermissionsResponse> {
    return this.http.get(`/permission/${orgNo}/${objectId}`).then((res) => res.data);
  }

  changePermissions(orgNo: string, request: ChangePermissionsRequest): Promise<ObjectPermission[]> {
    return this.http.post(`/permission/${orgNo}`, request).then((res) => res.data);
  }

  sendInvitation({
    invitationType,
    orgNo,
    recipients,
    language,
    roleId,
    personalNoteText,
  }: {
    invitationType: InvitationType;
    orgNo: string;
    recipients: InvitationRecipientRequest[];
    language: string;
    roleId?: string;
    personalNoteText?: string;
  }): Promise<void> {
    return this.http
      .post(`/invitation/${orgNo}/${invitationType}/invite`, {
        recipients,
        language,
        roleId,
        personalNoteText,
      })
      .then((res) => res.data);
  }

  inviteAdministratorOrCustomeRole({
    email,
    language,
    inviteTo,
    inviteToRoleId,
  }: {
    email: string;
    language: Language;
    inviteTo: string[];
    inviteToRoleId?: string;
  }): Promise<void> {
    return this.http
      .post(`/portfolio/admins/invite`, { email, language, inviteTo, inviteToRoleId })
      .then((res) => res.data);
  }

  fetchInvitation(invitationId: string, invitationToken: string): Promise<Invitation> {
    return this.http.get(`/invitation/${invitationId}/${invitationToken}`).then((res) => res.data);
  }

  acceptInvitation(invitationId: string, invitationToken: string): Promise<void> {
    return this.http
      .post(`/invitation/${invitationId}/${invitationToken}/accept`)
      .then((res) => res.data);
  }

  fetchBatchInvitation(invitationId: string, invitationToken: string): Promise<BatchInvitation> {
    return this.http
      .get(`/batch-invitation/${invitationId}/${invitationToken}`)
      .then((res) => res.data);
  }

  acceptBatchInvitation(invitationId: string, invitationToken: string): Promise<void> {
    return this.http
      .post(`/batch-invitation/${invitationId}/${invitationToken}/accept`)
      .then((res) => res.data);
  }

  fetchInvitationRequest(invitationRequestId: string): Promise<InvitationRequest> {
    return this.http
      .get(`/invitation/invitation-request/${invitationRequestId}`)
      .then((res) => res.data);
  }

  rejectInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/invitation-request/${invitationRequestId}/reject`)
      .then((res) => res.data);
  }

  fetchBatchInvitationRequest(batchId: string): Promise<InvitationRequest[]> {
    return this.http.get(`/invitation/batch-invitation-request/${batchId}`).then((res) => res.data);
  }

  acceptBatchInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/batch-invitation-request/${invitationRequestId}/accept`)
      .then((res) => res.data);
  }

  acceptInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/invitation-request/${invitationRequestId}/accept`)
      .then((res) => res.data);
  }

  rejectBatchInvitationRequest(invitationRequestId: string): Promise<void> {
    return this.http
      .post(`/invitation/batch-invitation-request/${invitationRequestId}/reject`)
      .then((res) => res.data);
  }

  sendEmailVerificationCode(emailAddress: string): Promise<void> {
    return this.http
      .post(`/user/send-verification-email`, { emailAddress })
      .then((res) => res.data);
  }

  updateShareholderPledge(id: number, form: ShareholderPledgeForm) {
    return this.http.put(`/shareholder/${id}/pledge`, form.toData()).then((res) => res.data);
  }

  updateShareholderRemark(id: number, form: ShareholderRemarksForm) {
    return this.http.put(`/shareholder/${id}/remark`, form.toData()).then((res) => res.data);
  }

  markAutoImportMessageAsRendered(organisationNumber: string): Promise<void> {
    return this.http
      .put(`/company/confirm-autoimport/${organisationNumber}`)
      .then((res) => res.data);
  }

  markAutoImportMessageAsNotRendered(organisationNumber: string): Promise<void> {
    return this.http
      .delete(`/company/confirm-autoimport/${organisationNumber}`)
      .then((res) => res.data);
  }

  updateUserLanguage(newLanguage: Language): Promise<void> {
    return this.http.put(`/user/language/${newLanguage}`).then((res) => res.data);
  }

  acceptCompanyTerms(organisationNumber: string): Promise<void> {
    return this.http.post(`/company/accept-terms/${organisationNumber}`).then((res) => res.data);
  }

  parseShareholdersExcel(orgNo: string, formData: FormData): Promise<ParseExcelResponse> {
    return this.http.post(`/shareholder/${orgNo}/parse-excel`, formData).then((res) => res.data);
  }

  uploadShareholdersExcel(
    orgNo: string,
    formData: FormData,
  ): Promise<UploadShareholdersExcelResponse> {
    return this.http
      .post(`/shareholder/${orgNo}/upload-shareholders`, formData)
      .then((res) => res.data);
  }

  importShareholders(
    orgNo: string,
    shareholders: ParsedShareholder[],
  ): Promise<ShareholderIdShares> {
    return this.http
      .post(`/shareholder/${orgNo}/import-shareholders`, {
        shareholders,
      })
      .then((res) => res.data);
  }

  storeShareholderField(id: number, name: ShareholderField, value: string): Promise<unknown> {
    return this.http
      .post(`/shareholder/update`, {
        shareholderId: id,
        fieldName: ShareholderField[name],
        newValue: value,
      })
      .then((res) => res.data);
  }

  addNewShareholder(
    orgNo: string,
    shareholderType: ShareholderType,
  ): Promise<NewShareholderResponse> {
    return this.http
      .post(`/shareholder/new-shareholder`, {
        shareholderType: shareholderType,
        orgNo: orgNo,
      })
      .then((res) => res.data);
  }

  addAffiliateToSession(affiliateKey: string): Promise<void> {
    return this.http.put(`/add-affiliate/${affiliateKey}`).then((res) => res.data);
  }

  deleteShareholderHard(shareholderId: number): Promise<unknown> {
    return this.http.delete(`/shareholder/delete-hard/${shareholderId}`).then((res) => res.data);
  }

  deleteTransaction(orgNumber: string, transactionlogId: number) {
    return this.http
      .delete(`/transaction/${orgNumber}/${transactionlogId}`)
      .then((res) => res.data);
  }

  updateShareholder(shareholderId: number, shareholderDTO: UpdateShareholderDTO) {
    return this.http.post(`/shareholder/${shareholderId}`, shareholderDTO).then((res) => res.data);
  }

  insertShareholder(shareholderDTO: UpdateShareholderDTO): Promise<ExtendedShareholderDTO> {
    return this.http.post(`/shareholder/new`, shareholderDTO).then((res) => res.data);
  }

  changeTransactionDate(orgNumber: string, transactionlogId: number, date: string) {
    return this.http
      .post(`/transaction/${orgNumber}/${transactionlogId}`, { transactionDate: date })
      .then((res) => res.data);
  }

  createPortfolio(portfolioName: string): Promise<Portfolio> {
    return this.http.post(`/portfolio/new`, { portfolioName }).then((res) => res.data);
  }

  createSigningDocumentBatch(documentReferences: string[]): Promise<string> {
    return this.http.post(`/sign/create-batch`, documentReferences).then((res) => res.data);
  }

  verifyUserEmail(emailAddress: string, verificationCode: string) {
    return this.http
      .post(`/user/verify-email`, { emailAddress, verificationCode })
      .then((res) => res.data);
  }

  getPortfolioShareholders(
    portfolio: SelectedPortfolioOption,
  ): Promise<PortfolioShareholdersResponse> {
    return this.http.get(`${toPortfolioApiUrl(portfolio)}/shareholders`).then((res) => res.data);
  }

  getPortfolioAdmins(portfolio: SelectedPortfolioOption): Promise<GetAdminsInCompaniesResponse> {
    return this.http.get(`${toPortfolioContactsApiUrl(portfolio)}/admins`).then((res) => res.data);
  }

  getPortfolioCustomRole(
    portfolio: SelectedPortfolioOption,
    portfolioId?: string,
  ): Promise<GetPortfolioCustomRoleRequest> {
    const url =
      portfolioId === undefined
        ? `${toPortfolioContactsApiUrl(portfolio)}/custom-roles`
        : `/portfolio/custom-roles?portfolioId=${portfolioId}`;

    return this.http.get(url).then((res) => res.data);
  }

  openPreviewCustomRole(roleId: string, orgNo: string) {
    return this.http
      .get(`company-settings/${orgNo}/preview-custom-role/${roleId}`)
      .then((res) => res.data);
  }

  updateAdministratorEmail({
    email,
    externalId,
    customRoleId,
    portfolioId,
  }: UpdateAdminEmailRequest): Promise<UpdateAdminEmailRequest> {
    return this.http
      .post(`/portfolio/admins/update-email`, {
        email,
        externalId,
        customRoleId,
        portfolioId,
      })
      .then((res) => res.data);
  }

  revokeAdminAccess(organizationNumber: string, externalId: string) {
    return this.http.delete(`/company-settings/${organizationNumber}/admin-access/${externalId}`);
  }

  revokeAdminAccessAll(externalId: string, orgNos: string[]): Promise<RevokeAllRequest[]> {
    return this.http
      .post(`/portfolio/admins/revoke-all`, {
        externalId,
        orgNos,
      })
      .then((res) => res.data);
  }

  registerShareholderContribution(shareholderContribution: ShareholderContribution) {
    return this.http
      .post(`/shareholderContribution/new`, shareholderContribution)
      .then((res) => res.data);
  }

  fetchShareholderContributions(organisationNumber: string): Promise<ShareholderContribution[]> {
    return this.http.get(`/shareholderContribution/${organisationNumber}`).then((res) => res.data);
  }

  fetchSingleShareholderContributions(
    organisationNumber: string,
    contributionId: number,
  ): Promise<ShareholderContributionDetailsResponse> {
    return this.http
      .get(`/shareholderContribution/${organisationNumber}/${contributionId}`)
      .then((res) => res.data);
  }

  uploadContributionAttachments(
    orgNumber: string,
    reference: string,
    form: FormData,
  ): Promise<UploadedAttachment[]> {
    return this.http
      .post(`/shareholderContribution/${orgNumber}/${reference}/attachments`, form)
      .then((res) => res.data);
  }

  downloadContributionAttachment(
    organisationNumber: string,
    reference: string,
    attachmentId: string,
  ) {
    return downloadFileContent(
      `shareholderContribution/${organisationNumber}/${reference}/attachments/${attachmentId}`,
      this.http,
    );
  }

  deleteContributionAttachment(
    organisationNumber: string,
    reference: string,
    attachmentId: string,
  ) {
    return this.http
      .delete(
        `/shareholderContribution/${organisationNumber}/${reference}/attachments/${attachmentId}`,
      )
      .then((res) => res.data);
  }

  changeContributionDate(
    organisationNumber: string,
    contributionId: number,
    registrationDate: string,
  ) {
    return this.http
      .post(`/shareholderContribution/${organisationNumber}/${contributionId}/changeDate`, {
        registrationDate,
      })
      .then((res) => res.data);
  }

  deleteContribution(organisationNumber: string, contributionId: number) {
    return this.http
      .delete(`/shareholderContribution/${organisationNumber}/${contributionId}`)
      .then((res) => res.data);
  }

  registerShareholderCommitment(shareholderCommitment: CreateShareholderCommitmentRequest) {
    return this.http
      .post(`/shareholder-commitment/new`, shareholderCommitment)
      .then((res) => res.data);
  }

  fetchShareholderCommitments(organisationNumber: string): Promise<ShareholderCommitment[]> {
    return this.http.get(`/shareholder-commitment/${organisationNumber}`).then((res) => res.data);
  }

  fetchSingleShareholderCommitments(
    organisationNumber: string,
    commitmentId: string,
  ): Promise<ShareholderCommitmentDetailsResponse> {
    return this.http
      .get(`/shareholder-commitment/${organisationNumber}/${commitmentId}`)
      .then((res) => res.data);
  }

  uploadCommitmentAttachments(
    orgNumber: string,
    commitmentId: string,
    form: FormData,
  ): Promise<UploadedAttachment[]> {
    return this.http
      .post(`/shareholder-commitment/${orgNumber}/${commitmentId}/attachments`, form)
      .then((res) => res.data);
  }

  downloadCommitmentAttachment(
    organisationNumber: string,
    commitmentId: string,
    attachmentId: string,
  ) {
    return downloadFileContent(
      `shareholder-commitment/${organisationNumber}/${commitmentId}/attachments/${attachmentId}`,
      this.http,
    );
  }

  deleteCommitmentAttachment(
    organisationNumber: string,
    commitmentId: string,
    attachmentId: string,
  ) {
    return this.http
      .delete(
        `/shareholder-commitment/${organisationNumber}/${commitmentId}/attachments/${attachmentId}`,
      )
      .then((res) => res.data);
  }

  changeCommitmentDate(organisationNumber: string, commitmentId: string, registrationDate: string) {
    return this.http
      .post(`/shareholder-commitment/${organisationNumber}/${commitmentId}/changeDate`, {
        registrationDate,
      })
      .then((res) => res.data);
  }

  deleteCommitment(organisationNumber: string, commitmentId: string) {
    return this.http
      .delete(`/shareholder-commitment/${organisationNumber}/${commitmentId}`)
      .then((res) => res.data);
  }

  deleteRepresentatives(shareholderId: number, representatives: number[]) {
    return this.http
      .post(`/shareholder/${shareholderId}/delete-representatives`, representatives)
      .then((res) => res.data);
  }

  fetchBankIdStatus() {
    return this.http.get(`/auth/bankid-status`).then((res) => res.data);
  }

  changeNominalValueForTransaction(
    orgNumber: string,
    transactionlogId: number,
    nominalValue: number,
  ) {
    return this.http
      .post(`/transaction/${orgNumber}/${transactionlogId}`, { nominalValue })
      .then((res) => res.data);
  }

  copyShareholders(
    toOrgNo: string,
    fromOrgNo: string,
    shareholderIds: number[],
  ): Promise<CopyShareholdersResponse> {
    return this.http
      .post(`/shareholder/${toOrgNo}/copy`, { toOrgNo, fromOrgNo, shareholderIds })
      .then((res) => res.data);
  }

  createEnterpriseAccount(request: EnterpriseAccountRequest) {
    return this.http.put(`/enterprise-accounts`, request).then((res) => res.data);
  }

  updateEnterpriseAccount(id: string, request: EnterpriseAccountRequest) {
    return this.http.post(`/enterprise-accounts/${id}`, request).then((res) => res.data);
  }

  findTripletexCustomer(orgNo: string) {
    return this.http
      .get(`/enterprise-accounts/find-tripletex-customer/${orgNo}`)
      .then((res) => res.data);
  }

  updateShareholderNames(orgNo: string, shareholderOrgNos: Set<string>): Promise<void> {
    return this.http
      .post(`/portfolio/follow-up/shareholders/update`, { orgNo, shareholderOrgNos })
      .then((res) => res.data);
  }
}

export type InvitationRecipientRequest = {
  email: string;
  shareholderId?: number;
  companyroleId?: number;
};

export type ShareholderContributionDetailsResponse = {
  contribution: ShareholderContribution;
  fileUploadConfig: FileUploadConfig;
  attachments: UploadedAttachment[];
  registeredBy: string;
};

export type ShareholderCommitmentDetailsResponse = {
  commitment: ShareholderCommitment;
  fileUploadConfig: FileUploadConfig;
  attachments: UploadedAttachment[];
  registeredBy: string;
};

export type BeneficialOwnersSearchResponse = {
  searchResults: BeneficialOwner[];
  newOwners: BeneficialOwner[];
  changedOwners: BeneficialOwner[];
  noLongerOwners: BeneficialOwner[];
  hasSubmittedStatement: boolean;
  lastSubmittedDate?: string;
  isPreviousStatementValid: boolean;
};

export type CopyShareholdersResponse = {
  shareholderIdMapping: {
    fromShareholderId: number;
    toShareholderId: number;
  }[];
};

export type UploadShareholdersExcelResponse = {
  shareholderIds: Array<number>;
  unmatchedRowCount: number;
  unchangedCount: number;
};

export type EnterpriseAccountRequest = {
  name: string;
  internalReferenceName?: string;
  status: EnterpriseAccountStatus;
  subscriptionLimits: { productId: string; limit: number }[];
  startTime: string;
  endTime?: string;
  contactName?: string;
  contactEmail?: string;
  primaryCompanyOrgNo: string;
  metadata: EnterpriseAccountMetaData;
  billingFrequency: BillingFrequency;
};
